if (!customElements.get('mirthly-editorial')) {
  class MirthlyEditorial extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mirthlyInitialized === 'true') return;
      this.dataset.mirthlyInitialized = 'true';
      const header = this.querySelector('.header');
      const menu = this.querySelector('.menu');
      const nav = this.querySelector('.header nav');
      const dropdowns = [...this.querySelectorAll('[data-nav-dropdown]')];
      const closeDropdowns = exception => dropdowns.forEach(dropdown => {
        if (dropdown === exception) return;
        dropdown.classList.remove('open');
        dropdown.querySelector('.nav-dropdown-toggle')?.setAttribute('aria-expanded', 'false');
      });
      const setMenu = open => {
        nav?.classList.toggle('open', open);
        menu?.classList.toggle('open', open);
        menu?.setAttribute('aria-expanded', String(open));
        menu?.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        document.body.classList.toggle('mirthly-menu-open', open);
        if (!open) closeDropdowns();
      };
      if (menu) menu.onclick = event => {
        event.stopPropagation();
        setMenu(!nav?.classList.contains('open'));
      };
      dropdowns.forEach(dropdown => {
        const toggle = dropdown.querySelector('.nav-dropdown-toggle');
        const setDropdown = open => {
          closeDropdowns(open ? dropdown : null);
          dropdown.classList.toggle('open', open);
          toggle?.setAttribute('aria-expanded', String(open));
        };
        if (toggle) toggle.onclick = event => {
          event.stopPropagation();
          setDropdown(!dropdown.classList.contains('open'));
        };
        dropdown.addEventListener('keydown', event => {
          if (event.key === 'Escape') {
            setDropdown(false);
            toggle?.focus();
          }
        });
      });
      document.addEventListener('click', event => {
        if (!nav?.contains(event.target) && !menu?.contains(event.target)) {
          closeDropdowns();
          if (window.matchMedia('(max-width: 820px)').matches) setMenu(false);
        }
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          closeDropdowns();
          setMenu(false);
        }
      });
      const syncHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 40);
      syncHeader();
      window.addEventListener('scroll', syncHeader, { passive: true });
      const slider = this.querySelector('[data-product-slider]');
      if (slider) {
        const slides = [...slider.querySelectorAll('[data-product-slide]')];
        const dots = [...slider.querySelectorAll('[data-product-dot]')];
        let current = 0;
        let timer;
        const showSlide = index => {
          if (!slides.length) return;
          current = (index + slides.length) % slides.length;
          slides.forEach((slide, slideIndex) => {
            const active = slideIndex === current;
            slide.classList.toggle('active', active);
            slide.setAttribute('aria-hidden', String(!active));
          });
          dots.forEach((dot, dotIndex) => {
            const active = dotIndex === current;
            dot.classList.toggle('active', active);
            dot.setAttribute('aria-current', String(active));
          });
        };
        const stop = () => window.clearInterval(timer);
        const start = () => {
          stop();
          if (slides.length > 1) {
            timer = window.setInterval(() => showSlide(current + 1), 5000);
          }
        };
        dots.forEach((dot, index) => dot.addEventListener('click', () => {
          showSlide(index);
          start();
        }));
        slider.addEventListener('focusin', stop);
        slider.addEventListener('focusout', start);
        showSlide(0);
        start();
      }
      this.querySelectorAll('[data-showcase-slider]').forEach((showcase, showcaseIndex) => {
        const slides = [...showcase.querySelectorAll('[data-showcase-slide]')];
        const dots = [...showcase.querySelectorAll('[data-showcase-dot]')];
        if (!slides.length) return;
        let current = Math.min(Number(showcase.dataset.startIndex) || 0, slides.length - 1);
        let timer;
        const show = index => {
          current = (index + slides.length) % slides.length;
          slides.forEach((slide, i) => {
            const active = i === current;
            slide.classList.toggle('active', active);
            slide.setAttribute('aria-hidden', String(!active));
          });
          dots.forEach((dot, i) => dot.classList.toggle('active', i === current));
        };
        const restart = () => {
          window.clearInterval(timer);
          if (slides.length > 1) timer = window.setInterval(() => show(current + 1), 5000);
        };
        dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); restart(); }));
        showcase.addEventListener('focusin', () => window.clearInterval(timer));
        showcase.addEventListener('focusout', restart);
        show(current);
        window.setTimeout(restart, showcaseIndex * 700);
      });

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reducedMotion && 'IntersectionObserver' in window) {
        const motionTargets = [...this.querySelectorAll([
          'main > section:not(.shoe-hero):not(.hero):not(.showcase-carousel)',
          '.catalog-grid article',
          '.faq details',
          '.mirthly-contact-shell > *'
        ].join(','))].filter(element =>
          !element.closest('[data-product-slide]') &&
          !element.closest('[data-showcase-slide]') &&
          !element.matches('[data-product-slider], [data-showcase-slider]')
        );

        if (motionTargets.length) {
          this.classList.add('mirthly-motion-ready');
          motionTargets.forEach((element, index) => {
            element.classList.add('mirthly-motion-item');
            if (element.matches('figure, .gallery, .transformation-media')) {
              element.classList.add('mirthly-motion-image');
            }
            element.style.setProperty('--mirthly-delay', `${(index % 4) * 70}ms`);
          });

          const motionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              motionObserver.unobserve(entry.target);
            });
          }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

          motionTargets.forEach(element => motionObserver.observe(element));
        }
      }

      const form = this.querySelector('[data-product-form]');
      if (!form) return;
      const variants = JSON.parse(form.querySelector('[data-variants]').textContent);
      const stockDataElement = form.querySelector('[data-variant-stock]');
      const stockData = stockDataElement ? JSON.parse(stockDataElement.textContent) : {};
      const variantId = form.querySelector('[name="id"]');
      const prices = this.querySelectorAll('.price [data-live-price], .editorial-sticky-atc [data-live-price]');
      const variantLabels = this.querySelectorAll('[data-live-variant]');
      const compare = this.querySelector('[data-live-compare]');
      const addButtons = this.querySelectorAll('[data-live-add]');
      const stickyBar = this.querySelector('[data-editorial-sticky]');
      const mainImage = this.querySelector('[data-main-image]');
      const quantity = form.querySelector('[name="quantity"]');
      const stockStatus = form.querySelector('[data-stock-status]');
      const stockMessage = form.querySelector('[data-stock-message]');
      const chosen = {};
      const options = variant => [variant.option1, variant.option2, variant.option3].filter(value => value != null);
      const primaryAddButton = form.querySelector('[data-live-add]');
      if (stickyBar && primaryAddButton && 'IntersectionObserver' in window) {
        new IntersectionObserver(entries => entries.forEach(entry => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        }), { threshold: 0 }).observe(primaryAddButton);
      }
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents) / 100);
      const updateStockStatus = variant => {
        if (!stockStatus || !stockMessage) return;
        const stock = stockData[String(variant.id)] || { available: variant.available, tracked: false, quantity: null };
        const limited = stock.available && stock.tracked && Number(stock.quantity) > 0 && Number(stock.quantity) <= 10;
        stockStatus.classList.toggle('stock-status-pill--limited', limited);
        stockStatus.classList.toggle('stock-status-pill--sold-out', !stock.available);
        stockMessage.textContent = !stock.available ? 'Sold out' : limited ? 'Limited stock available' : 'In stock';
      };
      const selectVariant = () => {
        const variant = variants.find(item => options(item).every((value, index) => chosen[index] === value));
        if (!variant) return;
        variantId.value = variant.id;
        variantLabels.forEach(element => { element.textContent = variant.title === 'Default Title' ? 'Ready to order' : variant.title; });
        prices.forEach(element => { element.textContent = money(variant.price); });
        const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
        if (compare) {
          compare.hidden = !hasCompare;
          compare.textContent = hasCompare ? money(variant.compare_at_price) : '';
        }
        addButtons.forEach(button => {
          button.disabled = !variant.available;
          button.textContent = variant.available ? `Add to cart · ${money(variant.price)}` : 'Sold out';
        });
        updateStockStatus(variant);
        if (mainImage) {
          const media = variant.featured_media?.preview_image;
          const image = variant.featured_image;
          let nextSrc = media?.src || image?.src;
          let nextAlt = variant.featured_media?.alt || image?.alt || variant.name || '';
          let matchingThumb = null;
          if (!nextSrc) {
            const selectedColor = options(variant).find(value => value === 'Soft White' || value === 'Charcoal Black');
            if (selectedColor) {
              matchingThumb = Array.from(this.querySelectorAll('[data-thumb]')).find(thumb =>
                (thumb.dataset.alt || '').toLowerCase().includes(selectedColor.toLowerCase())
              );
              if (matchingThumb) {
                nextSrc = matchingThumb.dataset.src;
                nextAlt = matchingThumb.dataset.alt || nextAlt;
              }
            }
          }
          if (nextSrc) {
            mainImage.src = nextSrc.startsWith('//') ? `${location.protocol}${nextSrc}` : nextSrc;
            mainImage.srcset = '';
            mainImage.alt = nextAlt;
            const syncedThumb = matchingThumb || galleryThumbs.find(thumb => {
              const thumbSrc = thumb.dataset.src || '';
              const normalizedNext = String(nextSrc).split('?')[0];
              return thumbSrc.split('?')[0] === normalizedNext || normalizedNext.includes(thumbSrc.split('?')[0]);
            });
            if (syncedThumb) {
              galleryIndex = galleryThumbs.indexOf(syncedThumb);
              galleryThumbs.forEach(thumb => thumb.classList.toggle('active', thumb === syncedThumb));
            }
          }
        }
      };
      this.querySelectorAll('[data-option-index]').forEach(button => button.addEventListener('click', () => {
        const index = Number(button.dataset.optionIndex);
        chosen[index] = button.dataset.value;
        this.querySelectorAll(`[data-option-index="${index}"]`).forEach(item => item.classList.toggle('selected', item === button));
        selectVariant();
      }));
      this.querySelectorAll('[data-option-index].selected').forEach(button => {
        chosen[Number(button.dataset.optionIndex)] = button.dataset.value;
      });
      this.querySelector('[data-minus]')?.addEventListener('click', () => {
        quantity.value = Math.max(1, (Number(quantity.value) || 1) - 1);
      });
      this.querySelector('[data-plus]')?.addEventListener('click', () => {
        quantity.value = Math.min(10, (Number(quantity.value) || 1) + 1);
      });
      const galleryThumbs = [...this.querySelectorAll('[data-thumb]')];
      const galleryOpen = this.querySelector('[data-gallery-open]');
      const lightbox = this.querySelector('[data-editorial-lightbox]');
      const lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
      const galleryClose = lightbox?.querySelector('[data-gallery-close]');
      const galleryPrevious = lightbox?.querySelector('[data-gallery-prev]');
      const galleryNext = lightbox?.querySelector('[data-gallery-next]');
      if (lightbox && lightbox.parentElement !== document.body) document.body.appendChild(lightbox);
      let galleryIndex = Math.max(0, galleryThumbs.findIndex(button => button.classList.contains('active')));
      let galleryTouchStart = 0;
      let galleryTouchMoved = false;
      const showGalleryImage = index => {
        if (!galleryThumbs.length || !mainImage) return;
        galleryIndex = (index + galleryThumbs.length) % galleryThumbs.length;
        const button = galleryThumbs[galleryIndex];
        galleryThumbs.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === galleryIndex));
        mainImage.src = button.dataset.src;
        mainImage.srcset = '';
        mainImage.alt = button.dataset.alt || '';
        if (lightboxImage && lightbox && !lightbox.hidden) {
          lightboxImage.src = button.dataset.src;
          lightboxImage.alt = button.dataset.alt || '';
        }
      };
      galleryThumbs.forEach((button, index) => button.addEventListener('click', () => showGalleryImage(index)));
      const closeGallery = () => {
        if (!lightbox) return;
        lightbox.hidden = true;
        document.body.classList.remove('mirthly-lightbox-open');
        galleryOpen?.focus();
      };
      galleryOpen?.addEventListener('click', () => {
        if (galleryTouchMoved || !lightbox || !lightboxImage || !galleryThumbs.length) return;
        lightbox.hidden = false;
        document.body.classList.add('mirthly-lightbox-open');
        lightboxImage.src = galleryThumbs[galleryIndex].dataset.src;
        lightboxImage.alt = galleryThumbs[galleryIndex].dataset.alt || '';
        galleryClose?.focus();
      });
      galleryOpen?.addEventListener('touchstart', event => {
        galleryTouchStart = event.changedTouches[0].clientX;
        galleryTouchMoved = false;
      }, { passive: true });
      galleryOpen?.addEventListener('touchend', event => {
        const distance = event.changedTouches[0].clientX - galleryTouchStart;
        galleryTouchMoved = Math.abs(distance) > 42;
        if (galleryTouchMoved) showGalleryImage(galleryIndex + (distance < 0 ? 1 : -1));
        window.setTimeout(() => { galleryTouchMoved = false; }, 80);
      }, { passive: true });
      galleryClose?.addEventListener('click', closeGallery);
      galleryPrevious?.addEventListener('click', () => showGalleryImage(galleryIndex - 1));
      galleryNext?.addEventListener('click', () => showGalleryImage(galleryIndex + 1));
      lightbox?.addEventListener('click', event => { if (event.target === lightbox) closeGallery(); });
      document.addEventListener('keydown', event => {
        if (!lightbox || lightbox.hidden) return;
        if (event.key === 'Escape') closeGallery();
        if (event.key === 'ArrowLeft') showGalleryImage(galleryIndex - 1);
        if (event.key === 'ArrowRight') showGalleryImage(galleryIndex + 1);
      });
      this.querySelectorAll('[data-select-option]').forEach(trigger => trigger.addEventListener('click', () => {
        const value = trigger.dataset.selectOption;
        const optionButton = [...this.querySelectorAll('[data-option-index]')].find(button => button.dataset.value === value);
        if (!optionButton) return;
        optionButton.click();
        this.querySelector('#buy')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
      selectVariant();
    }
  }
  customElements.define('mirthly-editorial', MirthlyEditorial);
}
